import { Box, Card, Flex, Grid, GridCell, Separator, SubTitle, Text } from "@shared/primitive-components"
import { localDateTimeToString } from "@shared/utils/formatters/dates"
import { navigationWithAltClick } from "@shared/utils/helpers/navigationWithAltClick"
import { Trip } from "../../../api/insights"
import { PATHS } from "../../../const/paths"
import { useAuth, usePolicyAndFleet } from "../../../contexts"
import { calculateScoreChange } from "../../../utils"
import { tripDetailsStyles as styles } from "../../styles"
import { Score } from "."
import { MaybeObfuscatedValue } from "../../../components/Obfuscated"

export const TRIP_DETAILS_DTI = {
  VRN: `trip_details_vrn`,
  MAKE_MODEL: `trip_details_model`,
  DISTANCE_DRIVEN: `trip_details_distance_driven`,
  TRIP_DATE: `trip_details_trip_date`,
  TRIP_TIMES: `trip_details_trip_times`,
  VEHICLE_SCORE: {
    SCORE: `trip_details_vehicle_score_score`,
    CHANGE: `trip_details_vehicle_score_change`,
  },
}

export type TripDetailsProps = {
  trip: Trip
}

export const TripDetailsCard = ({ trip }: TripDetailsProps) => {
  const { policy } = usePolicyAndFleet()
  const { getAuthType } = useAuth()
  const baseUrl = getAuthType() === `customer` ? `` : `/policy/${policy.id}`

  return (
    <Card css={styles.detailCard}>
      <Box css={styles.fullWidth}>
        <Flex alignItems="center">
          <SubTitle>Trip Details</SubTitle>
        </Flex>
        <Separator size="block" color="yellow" css={styles.marginBottom} />
        <Flex flexDirection="column" alignItems="start">
          <Grid css={styles.vehicleDetail}>
            <GridCell tablet={8} desktop={6}>
              <Box>
                <Text use="headline2" tag="h4" css={styles.detailHeading}>
                  Vehicle
                </Text>
                <Text
                  data-testid={TRIP_DETAILS_DTI.VRN}
                  onClick={(e) =>
                    navigationWithAltClick(
                      `/${PATHS.INSIGHTS.BASE}/${PATHS.INSIGHTS.VEHICLES}/${trip.vehicle.vrn.replace(/\s+/g, ``)}`,
                      e,
                      baseUrl
                    )
                  }
                  css={styles.vehicleLink}
                >
                  <MaybeObfuscatedValue type="vrn" value={trip.vehicle.vrn} />
                </Text>
                <Flex>
                  <Text use="body2" data-testid={TRIP_DETAILS_DTI.MAKE_MODEL}>
                    {trip.vehicle.name}
                  </Text>
                </Flex>
              </Box>
            </GridCell>
            <GridCell tablet={8} desktop={6}>
              <Box>
                <Text use="headline2" tag="h4" css={styles.detailHeading}>
                  Vehicle score
                </Text>
                <Score
                  score={trip.vehicle?.currentPeriod?.score ?? null}
                  change={calculateScoreChange(trip.vehicle.currentPeriod?.score, trip.vehicle.previousPeriodScore)}
                  dataTestIds={TRIP_DETAILS_DTI.VEHICLE_SCORE}
                />
              </Box>
            </GridCell>
          </Grid>
          <Box css={styles.vehicleDetail}>
            <Text use="headline2" tag="h4" css={styles.detailHeading}>
              Distance driven
            </Text>
            <Text data-testid={TRIP_DETAILS_DTI.DISTANCE_DRIVEN}>{trip.analytics.distance} miles</Text>
          </Box>
          <Box css={styles.vehicleDetail}>
            <Text use="headline2" tag="h4" css={styles.detailHeading}>
              Trip date and time
            </Text>
            <Text data-testid={TRIP_DETAILS_DTI.TRIP_DATE}>
              {localDateTimeToString(trip.startedAt, `en`, `dd/MM/yyyy`)}
            </Text>
            <Text data-testid={TRIP_DETAILS_DTI.TRIP_TIMES}>
              {`${localDateTimeToString(trip.startedAt, `en`, `h:mmaaa`)} - ${localDateTimeToString(
                trip.finishedAt,
                `en`,
                `h:mmaaa`
              )}`}
            </Text>
          </Box>
        </Flex>
      </Box>
    </Card>
  )
}
